import { useCallback, useMemo, useState } from "react";
import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import { useExperiment } from "@hopper-b2b/experiments";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { AirportMap } from "@hopper-b2b/types";
import {
  ActionLink,
  ButtonWrap,
  Divider,
  IconComponent,
  IconName,
  MobilePopoverCard,
} from "@hopper-b2b/ui";
import { EXPERIMENTS } from "../../../App";
import ChevronDown from "../../../assets/client/chevron-down.svg?react";
import InsuranceIcon from "../../../assets/client/uv/chubb.svg?react";
import { TravelInsuranceContactSupportModalContent } from "../../TravelInsuranceContactSupportModalContent";
import styles from "./styles.module.scss";

const TripFlightsDetailsInsurance = ({
  flight,
  isRoundTrip,
  airportMap,
}: {
  flight: BookedFlightItineraryWithDepartureTime;
  isRoundTrip: boolean;
  airportMap: AirportMap;
}) => {
  const { t, brand } = useI18nContext();

  const [contactSupportOpen, setContactSupportOpen] = useState(false);

  const openContactSupportHandler = useCallback(() => {
    setContactSupportOpen(true);
  }, []);

  const closeContactSupportHandler = useCallback(() => {
    setContactSupportOpen(false);
  }, []);

  const enableTravelInsurance = useExperiment(
    EXPERIMENTS.NUBANK_TRAVEL_INSURANCE
  );

  // This is very similar to utils/isFlightDomesticToInternational.ts, however becaue we're in trips we need to do different lookups
  const isDomesticToInternational = useMemo(() => {
    // Grab the initial trip slice - regardless of one way or round trip we just need the first slice.
    const travelItineraryInitialSlice =
      flight.bookedItinerary.travelItinerary.slices[0];

    // Get the origin's airport code
    const originLocationAirportCode =
      travelItineraryInitialSlice?.segments[0]?.origin.locationCode;

    // Get the destination's airport code, .length-1 accounts for layovers
    const destinationLocationAirportCode =
      travelItineraryInitialSlice?.segments[
        travelItineraryInitialSlice.segments.length - 1
      ]?.destination.locationCode;

    if (!originLocationAirportCode || !destinationLocationAirportCode) {
      return false;
    }

    // Get the origin/destination's country code, which is in the airportMap
    const originAirport = airportMap[originLocationAirportCode];
    const destinationAirport = airportMap[destinationLocationAirportCode];

    if (
      !originAirport?.geography.countryCode ||
      !destinationAirport?.geography.countryCode
    ) {
      return false;
    }

    const originAirportCountryCode = originAirport.geography.countryCode;
    const destinationAirportCountryCode =
      destinationAirport.geography.countryCode;

    // If both airport country codes are the same, it's not an international flight
    if (originAirportCountryCode === destinationAirportCountryCode) {
      return false;
    }
    // We have two separate countries, if the origin airport matches the preferred country code, the flight begins as domestic
    return originAirportCountryCode === brand?.preferredCountryCode;
  }, [airportMap, brand?.preferredCountryCode, flight]);

  return enableTravelInsurance && isDomesticToInternational ? (
    <>
      <Divider className={styles.Divider} />

      <h4 className={styles.SectionTitle}>
        {t("insuranceBottomEntry.sectionTitle")}
      </h4>

      <ButtonWrap
        onClick={openContactSupportHandler}
        className={styles.TripFlightsDetailsInsurance}
      >
        <div className="insurance-icon">
          <InsuranceIcon />
        </div>

        <span className="label">{t("insuranceBottomEntry.label")}</span>
        <div className="chevron">
          <ChevronDown />
        </div>
      </ButtonWrap>

      <MobilePopoverCard
        open={contactSupportOpen}
        className="contact-support-modal"
        scroll="paper"
        fullScreen
        topRightButton={
          <ActionLink
            className="introduction-modal-close-button"
            content={
              <IconComponent
                ariaLabel={t("modalClose.ariaLabel")}
                className={"close-button-icon"}
                name={IconName.Close}
              />
            }
            label={t("modalClose.label")}
            onClick={closeContactSupportHandler}
          />
        }
        onClose={closeContactSupportHandler}
      >
        <TravelInsuranceContactSupportModalContent />
      </MobilePopoverCard>
    </>
  ) : null;
};

export default TripFlightsDetailsInsurance;
